import React, { useState } from 'react';
import { Button } from '@ygtechnologies/rubus-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faCheck,
    faCircleCheck,
    faCircleXmark,
    faCommentSms,
    faCopy,
    faEnvelope,
    faFlask,
    faSparkles,
    faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import { FlatCard } from '@/components/FlatCard';
import {
    getAwsHealth,
    getLinearIssues,
    getSentryIssues,
    describeError,
    AwsHealth,
    AwsServiceHealth,
} from '@/services/systemHealth';
import { getCommunicoreSummary } from '@/services/communicore';
import { deliveryStats, ModuleState, ModuleStatus } from './customersDummyData';
import { SentryIssue, sentryIssues as dummySentryIssues } from './dummyData';
import { LinearIssue, linearIssues as dummyLinearIssues } from './activityDummyData';
import { isComing, isDone, useSource } from './devActivity';
import { SourceBadge } from './SourceBadge';
import shared from './SystemHealthPage.module.scss';
import theme from './CustomersTab.module.scss';

const stateIcon: Record<ModuleState, IconDefinition> = {
    ok: faCircleCheck,
    degraded: faTriangleExclamation,
    down: faCircleXmark,
};

const stateLabel: Record<ModuleState, string> = {
    ok: 'Working',
    degraded: 'Partly affected',
    down: 'Down',
};

const RELEASE_ROWS = 10;
const ERROR_ROWS = 8;

/** Customer-facing modules and where their errors and servers show up. */
const modules = [
    { module: 'Change Management', sentry: ['cm', 'cm-frontend', 'chat'], eb: ['bygr-api', 'bygr-api-chat'] },
    { module: 'Aftermarket', sentry: ['am', 'am-frontend'], eb: ['bygr-api-am'] },
    { module: 'Quality Management', sentry: ['qm', 'qm-frontend', 'qm-app'], eb: ['bygr-api-qm'] },
    { module: 'Sales', sentry: [], eb: ['sales-api', 'sales-gateway-api'] },
    { module: 'Core and Flatfinder', sentry: ['core'], eb: ['bygr-api-core'] },
    { module: 'Surveys', sentry: ['survey'], eb: ['rubus-survey'] },
    { module: 'Emails and SMS', sentry: ['communicore'], eb: ['rubus-api-communicore'] },
    { module: 'Ruben', sentry: ['ruben'], eb: ['bygr-api-ruben'] },
];
const moduleOf = (project: string) => modules.find((m) => m.sentry.includes(project))?.module ?? project;

// ponytail: "affected" = server unhealthy, or errors in the last hour that hit 5+ users today; tune if it cries wolf
const RECENT_MS = 60 * 60_000;
const USERS_AFFECTED = 5;

type Status = ModuleStatus & { users: number };

function moduleStatus(
    m: (typeof modules)[number],
    services: AwsServiceHealth[],
    issues: SentryIssue[],
    now: number
): Status {
    const envs = services.filter((s) => m.eb.includes(s.name));
    const recent = issues.filter(
        (i) => m.sentry.includes(i.project) && i.level !== 'warning' && now - i.lastSeen.getTime() < RECENT_MS
    );
    const users = recent.reduce((n, i) => n + i.users, 0);
    const down = envs.find((e) => e.health === 'down');
    const sick = down ?? envs.find((e) => e.health === 'degraded');
    const state: ModuleState = down ? 'down' : sick || users >= USERS_AFFECTED ? 'degraded' : 'ok';
    if (state === 'ok') return { module: m.module, state, summary: 'Working normally.', users: 0 };
    const summary = sick
        ? `${sick.environmentName} is ${sick.healthStatus ?? sick.health}${sick.causes[0] ? `: ${sick.causes[0]}` : ''}`
        : `${recent.length} error${recent.length === 1 ? '' : 's'} in the last hour, ${users} users hit today. Top: ${recent[0].title}`;
    return {
        module: m.module,
        state,
        summary,
        users,
        customerMessage:
            state === 'down'
                ? `${m.module} is unavailable right now. We're working on it and will let you know as soon as it's back.`
                : `Some users are seeing errors in ${m.module}. We're looking into it and will update you shortly.`,
    };
}

const loadProdAws = () =>
    getAwsHealth('prod').then(
        (data) => ({ data: data as AwsHealth | null, error: null }),
        (e) => ({ data: null, error: describeError(e) })
    );
const loadProdDeliveries = () =>
    getCommunicoreSummary().then(
        (s) => {
            const prod = s.environments.find((e) => e.stage === 'prod');
            return {
                data: prod?.stats ?? null,
                error: prod ? prod.error : 'No prod environment in CommunicoreEnvironments.',
            };
        },
        (e) => ({ data: null, error: describeError(e) })
    );
const loadProdSentry = () => getSentryIssues('Production').then((r) => ({ data: r.issues, error: r.error }));

const fmt = (n: number) => n.toLocaleString('en-GB');
const pct = (failed: number, sent: number) => `${((failed / Math.max(1, sent)) * 100).toFixed(1)}%`;

function timeAgo(date: Date, now: number) {
    const m = Math.max(0, Math.round((now - date.getTime()) / 60_000));
    if (m < 60) return `${m}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    return `${Math.floor(m / 1440)}d ago`;
}

const CustomersTab: React.FC<{ now: number }> = ({ now }) => {
    const [aws] = useSource(loadProdAws, null);
    const [sentry] = useSource(loadProdSentry, dummySentryIssues);
    const statusLive = aws.live && sentry.live;
    const statuses = modules.map((m) => moduleStatus(m, aws.data?.services ?? [], sentry.data, now));
    const affectedModules = statuses.filter((m) => m.state !== 'ok');
    const workingModules = statuses.filter((m) => m.state === 'ok');
    const affectedUsers = affectedModules.reduce((n, m) => n + m.users, 0);
    const errors = [...sentry.data].sort((a, b) => b.users - a.users).slice(0, ERROR_ROWS);
    // Only Linear: useDevActivity would also poll GitHub, which this tab doesn't show.
    const [linear] = useSource(getLinearIssues, dummyLinearIssues);
    const shipped = linear.data.filter(isDone);
    const coming = linear.data.filter(isComing);
    const [deliveries] = useSource(loadProdDeliveries, deliveryStats);
    const { outgoingEmails: email, outgoingSms: sms } = deliveries.data;
    const deliveryErrors = [
        ...email.topErrors.map((e) => ({ ...e, channel: 'Email' })),
        ...sms.topErrors.map((e) => ({ ...e, channel: 'SMS' })),
    ].sort((a, b) => b.count - a.count);

    return (
        <div className={theme.tab}>
            <p className={theme.headline}>
                <SourceBadge live={statusLive} stale={aws.stale || sentry.stale} />{' '}
                {affectedModules.length === 0 ? (
                    'Everything is working normally for customers.'
                ) : (
                    <>
                        <strong>
                            {affectedModules.length} module{affectedModules.length === 1 ? '' : 's'} affected:
                        </strong>{' '}
                        {affectedModules
                            .map((m) => m.module)
                            .join(', ')
                            .replace(/, ([^,]*)$/, ' and $1')}
                        {affectedUsers > 0 && `, about ${affectedUsers} users hit today`}.
                    </>
                )}
            </p>

            {affectedModules.length > 0 && (
                <section className={theme.incidents}>
                    {affectedModules.map((m) => (
                        <div key={m.module} className={`${theme.module} ${theme[m.state]}`}>
                            <div className={theme.moduleHead}>
                                <FontAwesomeIcon icon={stateIcon[m.state]} className={theme.moduleIcon} />
                                <span className={theme.moduleName}>{m.module}</span>
                                <span className={theme.moduleState}>{stateLabel[m.state]}</span>
                            </div>
                            <p className={theme.moduleSummary}>{m.summary}</p>
                            {m.customerMessage && <CopyMessage text={m.customerMessage} />}
                        </div>
                    ))}
                </section>
            )}

            <section className={theme.workingRow}>
                <span className={theme.workingLabel}>Working normally</span>
                {workingModules.map((m) => (
                    <span key={m.module} className={theme.workingChip}>
                        <FontAwesomeIcon icon={faCircleCheck} /> {m.module}
                    </span>
                ))}
            </section>

            <div className={theme.row}>
                <FlatCard
                    title="Errors customers hit (prod, 24h)"
                    actions={<SourceBadge live={sentry.live} stale={sentry.stale} />}>
                    <div className={theme.tableWrap}>
                        <table className={shared.table}>
                            <thead>
                                <tr>
                                    <th>Error</th>
                                    <th className={shared.num}>Users</th>
                                    <th className={shared.num}>Last seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {errors.map((i) => (
                                    <tr key={i.shortId}>
                                        <td>
                                            {i.permalink ? (
                                                <a href={i.permalink} target="_blank" rel="noreferrer">
                                                    {i.title}
                                                </a>
                                            ) : (
                                                i.title
                                            )}
                                            <div className={shared.muted}>{moduleOf(i.project)}</div>
                                        </td>
                                        <td className={shared.num}>{i.users}</td>
                                        <td className={`${shared.num} ${shared.muted}`}>{timeAgo(i.lastSeen, now)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </FlatCard>

                <FlatCard
                    title="Emails and SMS (24h)"
                    actions={<SourceBadge live={deliveries.live} stale={deliveries.stale} />}>
                    <div className={theme.deliveryTiles}>
                        <DeliveryTile icon={faEnvelope} label="Email" {...email} />
                        <DeliveryTile icon={faCommentSms} label="SMS" {...sms} />
                    </div>
                    <h5 className={theme.subheading}>Top errors</h5>
                    {deliveryErrors.length === 0 ? (
                        <p className={shared.muted}>No failed sends.</p>
                    ) : (
                        <ul className={theme.failures}>
                            {deliveryErrors.map((f) => (
                                <li key={`${f.channel}-${f.error}`}>
                                    <FontAwesomeIcon
                                        icon={f.channel === 'Email' ? faEnvelope : faCommentSms}
                                        className={theme.failureIcon}
                                        fixedWidth
                                    />
                                    <div className={theme.failureBody}>
                                        <div>{f.error}</div>
                                        <div className={shared.muted}>
                                            {f.channel}, last {timeAgo(new Date(f.lastAt), now)}
                                        </div>
                                    </div>
                                    <span className={theme.failureCount}>{f.count}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </FlatCard>
            </div>

            <div className={theme.row}>
                <FlatCard
                    title={
                        <span className={theme.cardTitle}>
                            <FontAwesomeIcon icon={faSparkles} /> Shipped this week
                        </span>
                    }
                    actions={<SourceBadge live={linear.live} stale={linear.stale} />}>
                    <ReleaseList issues={shipped} now={now} />
                </FlatCard>
                <FlatCard
                    title={
                        <span className={theme.cardTitle}>
                            <FontAwesomeIcon icon={faFlask} /> Coming soon
                        </span>
                    }
                    actions={<SourceBadge live={linear.live} stale={linear.stale} />}>
                    <p className={theme.cardNote}>Being tested now; likely live within a few days.</p>
                    <ReleaseList issues={coming} now={now} />
                </FlatCard>
            </div>
        </div>
    );
};

const CopyMessage: React.FC<{ text: string }> = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const copy = () =>
        navigator.clipboard
            ?.writeText(text)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {});
    return (
        <div className={theme.message}>
            <p className={theme.messageText}>“{text}”</p>
            <Button size="xs" variant="secondary" icon={copied ? faCheck : faCopy} onClick={copy}>
                {copied ? 'Copied' : 'Copy message for customers'}
            </Button>
        </div>
    );
};

const DeliveryTile: React.FC<{ icon: IconDefinition; label: string; sent: number; failed: number }> = ({
    icon,
    label,
    sent,
    failed,
}) => {
    const bad = failed / Math.max(1, sent) > 0.01;
    return (
        <div className={shared.stat}>
            <div className={shared.statLabel}>
                <FontAwesomeIcon icon={icon} /> {label}
            </div>
            <div className={shared.statValue}>{fmt(sent)}</div>
            <div className={bad ? theme.failedBad : shared.muted}>
                {fmt(failed)} failed ({pct(failed, sent)})
            </div>
        </div>
    );
};

const ReleaseList: React.FC<{ issues: LinearIssue[]; now: number }> = ({ issues, now }) =>
    issues.length === 0 ? (
        <p className={shared.muted}>Nothing yet.</p>
    ) : (
        <ul className={theme.releases}>
            {issues.slice(0, RELEASE_ROWS).map((i) => (
                <li key={i.id}>
                    <span className={theme.releaseTitle}>{i.title}</span>
                    <span className={shared.muted}>
                        {i.id} · {timeAgo(i.updatedAt, now)}
                    </span>
                </li>
            ))}
            {issues.length > RELEASE_ROWS && <li className={shared.muted}>+{issues.length - RELEASE_ROWS} more</li>}
        </ul>
    );

export default CustomersTab;
